import { useRef, useState } from "react";
import type { Strings } from "../i18n/strings";
import type { Lang, MoonResponse } from "../lib/types";
import { captureCard, saveCard, shareCard } from "../lib/exportCard";
import { showRewarded } from "../lib/ads";
import { isNative } from "../lib/native";
import { track } from "../lib/analytics";
import KeepsakeCard from "./KeepsakeCard";

interface Props {
  t: Strings;
  lang: Lang;
  response: MoonResponse;
  onBack: () => void; // ← 예언으로
  onReplay: () => void;
}

// S5 · 카드 — 앞면=보편적 한 마디(공유 기본값). 편지 포함은 옵트인+고지. 추출 1회당 전면광고(게이트 2).
export default function Card({ t, response, onBack, onReplay }: Props) {
  const [letterIncluded, setLetterIncluded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<"" | "save" | "share">(""); // 진행 중 표시(클릭한 버튼에 스피너)
  const [saved, setSaved] = useState<"" | "ok" | "fail">("");
  const cardRef = useRef<HTMLDivElement>(null);

  async function extract(mode: "save" | "share") {
    if (!cardRef.current || busy) return;
    setBusy(true);
    setPending(mode);
    setSaved("");
    track(mode === "save" ? "card_saved" : "card_shared", { letter: letterIncluded ? 1 : 0 });

    // 게이트 2: 결과 추출(저장/공유) = 보상형 광고(네이티브). 웹은 Auto ads.
    // 끝까지 봄(earned)·광고 없음(unavailable)이면 진행. 중간에 닫으면(dismissed) 추출 안 함(보상 미획득).
    if (isNative()) {
      const r = await showRewarded();
      if (r === "dismissed") {
        setBusy(false);
        setPending("");
        return;
      }
    }

    try {
      const dataUrl = await captureCard(cardRef.current);
      if (mode === "save") {
        const ok = await saveCard(dataUrl);
        setSaved(ok ? "ok" : "fail");
      } else {
        // 캡션 = 보편적 '달의 한 마디'(프라이버시 안전) + 브랜드·태그라인·링크·해시태그.
        await shareCard(dataUrl, `${response.oneLine}\n\n${t.shareText}`);
      }
    } catch {
      if (mode === "save") setSaved("fail");
    }
    setBusy(false);
    setPending("");
  }

  return (
    <div className="screen card-screen">
      <div className="topbar">
        <button className="navbtn" onClick={onBack}>
          ← {t.back}
        </button>
        <button className="navbtn" onClick={onReplay}>
          {t.newConfession}
        </button>
      </div>

      <KeepsakeCard
        ref={cardRef}
        data={{
          oneLine: response.oneLine,
          letter: letterIncluded ? response.letter : undefined,
          prophecy: letterIncluded ? response.prophecy : undefined,
        }}
      />

      <div className={`toggle-row ${letterIncluded ? "on" : ""}`} onClick={() => setLetterIncluded((v) => !v)}>
        <span className="toggle-label">{t.toggleLetter}</span>
        <span className={`toggle-sw ${letterIncluded ? "on" : ""}`}>
          <span className="toggle-knob" />
        </span>
      </div>

      <div className="card-spacer" />

      <div className="card-actions">
        <button className="btn-primary" disabled={busy} onClick={() => extract("save")}>
          {pending === "save" ? (
            <span className="btn-spin" aria-label="…" />
          ) : (
            <>
              {t.keep}
              {saved === "ok" ? " ✓" : saved === "fail" ? " ✕" : ""}
            </>
          )}
        </button>
        <button className="btn-outline" disabled={busy} onClick={() => extract("share")}>
          {pending === "share" ? <span className="btn-spin" aria-label="…" /> : t.shareBtn}
        </button>
      </div>
    </div>
  );
}
